import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { getMails } from '@/services/api';
import { useRequest, useScroll } from 'ahooks';
import Loading from '../Loading';
import { forwardRef, useRef, useState, useEffect, useLayoutEffect, useCallback, useImperativeHandle } from 'react';
import ListWrap from './ListWrap';
import Excerpt from './emailMessage/Excerpt';
import { isCustomMessage, isEmpty } from '@/utils/utils';
import NewMessage from '../NewMessageTip';
import { debounce } from 'lodash-es';
import { useConversationContext } from '../../../context';
import { Button } from 'antd';
import EmailChat from './emailMessage/EmailChat';
import { extractBlockquote, formatSubjectPrefix } from './uitls';
const MailMessageList = /*#__PURE__*/ forwardRef((props, ref)=>{
    var _shopList_;
    useImperativeHandle(ref, ()=>({
            refresh: ()=>runAsync()
        }));
    const { id } = useCoversationIds();
    const shopList = useAppSelector((state)=>state.authorize.shopList);
    const shopId = (_shopList_ = shopList[0]) === null || _shopList_ === void 0 ? void 0 : _shopList_.id;
    const lastChat = useRef({});
    const hasMore = useRef(true);
    const scrollContainer = useRef(null);
    const listScrollInfo = useScroll(scrollContainer, (param)=>{
        let { top } = param;
        return unreadCount > 0 ? true : top < 300;
    });
    const [messageList, setMessageList] = useState([]);
    // websocket 收到的消息放在这里，不放在messageList是为了不影响现有逻辑
    const [newMessageList, setNewMessageList] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const initialScroll = useRef(true);
    const [firsrRequest, setFirstRequest] = useState(true);
    const { conversationInfo } = useConversationContext();
    const [timingInfo, setTimingInfo] = useState({});
    const isLast = (id)=>{
        var _messageList_concat;
        return ((_messageList_concat = messageList.concat(newMessageList)) === null || _messageList_concat === void 0 ? void 0 : _messageList_concat.at(-1).id) === id;
    };
    const prevScrollInfo = useRef({
        scrollTop: 0,
        scrollHeight: 0
    });
    // 拉取历史邮件
    const { loading, runAsync } = useRequest(async ()=>{
        if (!hasMore.current) {
            return;
        }
        const { list, has_more, scheduled_task } = await getMails({
            conversation_no: id,
            jk_shop_id: shopId,
            page_size: 10,
            type: 'pre',
            ...lastChat.current
        });
        setFirstRequest(false);
        if (list.length > 0) {
            lastChat.current.chat_id = list[0].id;
        }
        hasMore.current = has_more;
        // 首次请求把最后一条展开
        if (firsrRequest) {
            let lastArr = list[list.length - 1];
            lastArr.expand = true;
        }
        setMessageList((messageList)=>(list || []).concat(messageList));
        setTimingInfo(scheduled_task);
        const scroller = scrollContainer.current;
        prevScrollInfo.current = {
            scrollTop: scroller.scrollTop || 0,
            scrollHeight: scroller.scrollHeight || 0
        };
    });
    // 当 messageList 更新时，保持滚动位置
    useLayoutEffect(()=>{
        var _scrollContainer_current, _scrollContainer_current1;
        if (initialScroll.current) {
            return;
        }
        const prev = prevScrollInfo.current;
        const cur = {
            scrollHeight: ((_scrollContainer_current = scrollContainer.current) === null || _scrollContainer_current === void 0 ? void 0 : _scrollContainer_current.scrollHeight) || 0
        };
        const newScrollTop = prev.scrollTop + (cur.scrollHeight - prev.scrollHeight);
        (_scrollContainer_current1 = scrollContainer.current) === null || _scrollContainer_current1 === void 0 ? void 0 : _scrollContainer_current1.scrollTo({
            top: newScrollTop,
            behavior: 'instant'
        });
    }, [
        messageList
    ]);
    // 首次加载滚动到底部
    useEffect(()=>{
        if (initialScroll.current) {
            var _scrollContainer_current;
            initialScroll.current = false;
            (_scrollContainer_current = scrollContainer.current) === null || _scrollContainer_current === void 0 ? void 0 : _scrollContainer_current.scrollBy({
                top: Number.MAX_SAFE_INTEGER
            });
        }
    }, [
        messageList
    ]);
    // 滚动触发加载更多
    useEffect(()=>{
        if (!loading && hasMore.current && ((listScrollInfo === null || listScrollInfo === void 0 ? void 0 : listScrollInfo.top) || 0) < 300) {
            runAsync();
        }
    }, [
        listScrollInfo
    ]);
    // websocket 新邮件处理
    useConvNewMailMessage(id, (message)=>{
        setNewMessageList((list)=>list.concat(message));
        if (isCustomMessage(message.type)) {
            setUnreadCount((unreadCount)=>unreadCount + 1);
        }
        const el = scrollContainer.current;
        const delta = 100;
        if (el.scrollTop + el.clientHeight + delta >= el.scrollHeight) {
            debounce(()=>{
                el.scrollBy({
                    top: Number.MAX_SAFE_INTEGER,
                    behavior: 'smooth'
                });
            }, 50)();
        }
    });
    // 更新未读数量
    const updateUnreadCount = useCallback(debounce(()=>{
        setUnreadCount((unreadCount)=>{
            var _scrollContainer_current, _scrollContainer_current1, _scrollContainer_current2;
            if (unreadCount === 0) {
                return unreadCount;
            }
            const scrollTop = ((_scrollContainer_current = scrollContainer.current) === null || _scrollContainer_current === void 0 ? void 0 : _scrollContainer_current.scrollTop) || 0;
            const contentHeight = ((_scrollContainer_current1 = scrollContainer.current) === null || _scrollContainer_current1 === void 0 ? void 0 : _scrollContainer_current1.clientHeight) || 0;
            const nodes = ((_scrollContainer_current2 = scrollContainer.current) === null || _scrollContainer_current2 === void 0 ? void 0 : _scrollContainer_current2.childNodes) || [];
            let count = 0;
            for(let i = nodes.length - 1; i > 0; i--){
                const node = nodes[i];
                if (node.dataset.type === undefined) {
                    continue;
                }
                if (!isCustomMessage(Number(node.dataset.type))) {
                    continue;
                }
                if (node.offsetTop - scrollTop - node.clientHeight / 2 >= contentHeight) {
                    count++;
                } else {
                    break;
                }
            }
            return Math.min(unreadCount, count);
        });
    }, 300), []);
    useEffect(()=>{
        updateUnreadCount();
    }, [
        listScrollInfo,
        updateUnreadCount
    ]);
    const extractNo = !!(conversationInfo === null || conversationInfo === void 0 ? void 0 : conversationInfo.ai_extract);
    const isTiming = isEmpty(timingInfo);
    const dispatch = useAppDispatch();
    const [editingMail, setEditingMail] = useState(null);
    // 点击编辑触发
    const handleEditTiming = (data)=>{
        ;
        window['MAIL_REPLY_ID'] = data.reply_mail_log_id;
        const { body, outer } = extractBlockquote(data.content);
        dispatch(updateEditorContent({
            insertType: INSERT_TYPE.REPLY,
            subject: formatSubjectPrefix(data.subject, 'RE'),
            content: `${body}`,
            quote: `
                        <blockquote>
                            <div style="border-left: 1px solid rgb(204, 204, 204); padding-left: .5em;white-space:normal;mso-line-height-rule:exactly;">
                            ${outer}
                            </div>
                        </blockquote>`,
            isAI: false
        }));
        const params = {
            ...data,
            subject: formatSubjectPrefix(data.subject, 'RE'),
            content: body
        };
        setEditingMail(params);
    };
    const refreshList = useCallback(async ()=>{
        hasMore.current = true;
        lastChat.current = {};
        setMessageList([]);
        setNewMessageList([]);
        await runAsync();
    }, [
        runAsync
    ]);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            !isTiming && /*#__PURE__*/ _jsxs("div", {
                className: "bg-[#FEEFC9] flex items-center p-[10px]",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        children: t('c_e_send_time', {
                            date: timingInfo.send_time
                        })
                    }),
                    /*#__PURE__*/ _jsx(Button, {
                        type: "link",
                        onClick: ()=>handleEditTiming(timingInfo),
                        children: t('c_s_edit')
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "px-[10px] overflow-auto grow",
                ref: scrollContainer,
                children: [
                    /*#__PURE__*/ _jsx(Loading, {
                        loading: loading
                    }),
                    extractNo && /*#__PURE__*/ _jsx(Excerpt, {
                        text: conversationInfo === null || conversationInfo === void 0 ? void 0 : conversationInfo.ai_extract
                    }),
                    /*#__PURE__*/ _jsx(ListWrap, {
                        list: messageList,
                        isLast: isLast,
                        onRefresh: refreshList,
                        isTiming: timingInfo
                    }),
                    /*#__PURE__*/ _jsx(ListWrap, {
                        list: newMessageList,
                        isLast: isLast,
                        onRefresh: refreshList,
                        isTiming: timingInfo
                    }),
                    /*#__PURE__*/ _jsx(NewMessage, {
                        count: unreadCount,
                        visible: unreadCount > 0,
                        onClick: ()=>{
                            var _scrollContainer_current;
                            setUnreadCount(0);
                            (_scrollContainer_current = scrollContainer.current) === null || _scrollContainer_current === void 0 ? void 0 : _scrollContainer_current.scrollBy({
                                top: Number.MAX_SAFE_INTEGER,
                                behavior: 'smooth'
                            });
                        }
                    })
                ]
            }),
            editingMail && /*#__PURE__*/ _jsx(EmailChat, {
                onEvent: async ()=>{
                    setEditingMail(null);
                    await refreshList();
                },
                onRefresh: refreshList,
                id: editingMail.id,
                editingMail: editingMail,
                type: "update",
                isScheduledTask: isTiming
            }),
            props.children
        ]
    });
});
export default MailMessageList;
